import { Suspense, useContext } from 'react';
import { useOutletContext, useRoutes } from 'react-router-dom';

import BackToDashboardLink from '@/components/BackToDashboardLink';
import DelayedSuspenseFallback from '@/components/DelayedSuspenseFallback';
import { isDevFeaturesEnabled } from '@/consts/env';
import { AppThemeContext } from '@/contexts/AppThemeProvider';
import OverlayScrollbar from '@/ds-components/OverlayScrollbar';
import Tag from '@/ds-components/Tag';
import { useConsoleRoutes } from '@/hooks/use-console-routes';
import { usePlausiblePageview } from '@/hooks/use-plausible-pageview';
import useTenantPathname from '@/hooks/use-tenant-pathname';

import type { AppContentOutletContext } from '../AppContent/types';

import useTenantScopeListener from './hooks';
import styles from './index.module.scss';

const contextualBackLinkRoutes = [
  { pathname: '/applications/:id/guide/:guideId' },
  { pathname: '/applications/:id/:tab' },
  { pathname: '/api-resources/:id/guide/:guideId' },
  { pathname: '/api-resources/:id/:tab' },
  { pathname: '/audit-logs/:logId' },
  { pathname: '/connectors/:tab/:connectorId', exact: true },
  { pathname: '/customize-jwt/:tokenType/:action' },
  { pathname: '/enterprise-sso/:ssoConnectorId/:tab' },
  { pathname: '/organization-template/organization-roles/:id/:tab' },
  { pathname: '/organizations/:id/:tab' },
  { pathname: '/roles/:id/:tab' },
  { pathname: '/security/captcha/details' },
  { pathname: '/sign-in-experience/collect-user-profile/fields/:fieldName' },
  { pathname: '/users/:id/:tab' },
  { pathname: '/users/:userId/logs/:logId' },
  { pathname: '/users/:userId/sessions/:sessionId' },
  { pathname: '/users/:userId/social-identities/:target' },
  { pathname: '/users/:userId/sso-identities/:connectorId' },
  { pathname: '/webhooks/:hookId/recent-requests/:logId' },
  { pathname: '/webhooks/:id/:tab' },
];

function ConsoleContent() {
  const { embeddedInPortal } = useContext(AppThemeContext);
  const { scrollableContent } = useOutletContext<AppContentOutletContext>();
  const routeObjects = useConsoleRoutes();
  const routes = useRoutes(routeObjects);
  const { match } = useTenantPathname();
  const isDashboard = match('/dashboard', true);
  const hasContextualBackLink = contextualBackLinkRoutes.some(({ pathname, exact }) =>
    match(pathname, exact)
  );

  usePlausiblePageview(routeObjects, ':tenantId');
  // Use this hook here to make sure console listens to user tenant scope changes.
  useTenantScopeListener();

  return (
    <div className={styles.content}>
      <OverlayScrollbar className={styles.overlayScrollbarWrapper}>
        <div ref={scrollableContent} className={styles.main}>
          {!embeddedInPortal && !isDashboard && !hasContextualBackLink && (
            <div className={styles.backLinkRow}>
              <BackToDashboardLink />
            </div>
          )}
          <Suspense fallback={<DelayedSuspenseFallback />}>{routes}</Suspense>
        </div>
      </OverlayScrollbar>
      {!embeddedInPortal && isDevFeaturesEnabled && (
        <Tag type="state" status="success" variant="plain" className={styles.devStatus}>
          Dev features enabled
        </Tag>
      )}
    </div>
  );
}

export default ConsoleContent;
